import Header from "../header/HeaderComponent"
import Footer from "../footer/FooterComponent"
import Seo from "../Seo/SeoComponent"
import styles from "./privacyPolicyPage.module.scss"

export default function PrivacyPolicyPage() {
    return (
        <div className={styles.page}>
            <Seo
                title="Privacy Policy"
                description="How NihongoTech handles personal information and analytics for users of the app."
                path="/privacy-policy"
            />
            <Header />

            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>Privacy Policy</h1>

                    <p className={styles.text}>
                        NihongoTech by TeqSeq provides Japanese learning and writing tools. You can
                        browse without an account. The information processed depends on the
                        features you use and your cookie preferences.
                    </p>

                    <h2>Cookies and browser storage</h2>
                    <p className={styles.text}>
                        We use cookies and similar browser storage for sign-in, privacy preferences,
                        learning settings, and optional analytics. Cookies are small files stored
                        by your browser; local storage keeps information on your device until it
                        is removed by the app or you clear your site data.
                    </p>
                    <p className={styles.text}>
                        AWS Cognito and Amplify store authentication tokens and session information
                        to keep you signed in. iubenda stores your cookie preferences. The app also
                        stores study progress, practice counts, notes, and your selected JLPT level
                        in your browser. These functions are separate from optional measurement cookies.
                        Signing out clears your authentication session; it does not erase all study
                        data saved in this browser.
                    </p>
                    <p className={styles.text}>
                        You can review or withdraw optional consent using <strong>Cookie settings</strong>
                        {" "}in the footer. You can also delete cookies and site storage in your browser;
                        doing so may sign you out and remove locally saved progress and connections.
                        Our <a href="https://www.iubenda.com/privacy-policy/32561228/cookie-policy">cookie policy</a>
                        {" "}provides further information about trackers and their duration.
                    </p>

                    <h2>Analytics and visit statistics</h2>
                    <p className={styles.text}>
                        We use Google Analytics 4, PostHog, and Sentry to understand which pages and learning
                        features people use and to diagnose technical errors. They process usage events, browser,
                        device, session, page, and error information. Analytics can use cookies or local storage to
                        recognize returning browsers, so this information is not necessarily anonymous. When you
                        sign in, we can associate analytics with an account identifier.
                    </p>
                    <p className={styles.text}>
                        PostHog and Sentry are enabled by measurement consent. Sentry session replay samples 10%
                        of sessions and records all sessions where an error occurs; it masks page text and inputs
                        and does not capture request or response bodies. Google Analytics is controlled through
                        iubenda's Google Consent Mode integration;
                        depending on that configuration, Google may receive cookieless measurement
                        signals when analytics storage is denied. We do not intentionally include
                        passwords, email addresses, or the text you type in our custom analytics events.
                        PostHog automatic click capture is disabled. Session recording is disabled
                        by default; if enabled, it uses measurement consent and masks page text and inputs.
                    </p>
                    <p className={styles.text}>
                        Separately, our AWS-hosted visit counter runs on page visits independently
                        of optional analytics consent. It records the page path, timestamp, referring
                        address, device, browser, operating system, language, screen and viewport sizes,
                        and timezone. It also records country and city when supplied in request headers.
                        Each visit receives a separate record ID; the counter does not set a browser
                        identifier or store your IP address in that visit record. Network and security
                        services still process IP addresses to deliver and protect the site.
                    </p>
                    <p className={styles.text}>
                        See the providers' information about their processing:
                        {" "}<a href="https://policies.google.com/privacy">Google Privacy Policy</a>,
                        {" "}<a href="https://posthog.com/privacy">PostHog Privacy Policy</a>, and
                        {" "}<a href="https://sentry.io/privacy/">Sentry Privacy Policy</a>.
                    </p>

                    <h2>Accounts and learning progress</h2>
                    <p className={styles.text}>
                        An optional account lets you save progress across devices. We collect your
                        email address and chosen display name. AWS Cognito handles password
                        authentication; our application backend does not store your password.
                        Google sign-in shares account information with Cognito to create or access
                        your account. Your progress and study notes are stored in our AWS account
                        using Amazon DynamoDB, linked to your account identifier. Existing browser
                        progress may be uploaded when you first sign in. You can change your display
                        name or password from your profile and contact us about account deletion.
                    </p>

                    <h2>Optional connected services</h2>
                    <p className={styles.text}>
                        If you connect WaniKani, your personal access token and cached progress are
                        stored in this browser and requests are sent directly to WaniKani's API.
                        Our application backend does not receive that token. Disconnecting removes
                        the stored WaniKani connection and cache.
                    </p>
                    <p className={styles.text}>
                        When you request translation, the submitted text is sent through our AWS
                        backend to Google Cloud Translation. Image text recognition can send your
                        selected image to Google Cloud Vision and, for signed-in users when the
                        AI-assisted fallback is used, Anthropic. Some recognition can run locally
                        in your browser. Cloud requests process the content you submit to return
                        the result; avoid submitting content you do not want those services to process.
                    </p>
                    <p className={styles.text}>
                        If you subscribe to the newsletter, we store your email address, subscription
                        status, and subscription or confirmation timestamps in AWS. Amazon SES sends
                        confirmation and newsletter emails. Newsletter subscription is separate from
                        cookie consent. You can unsubscribe using the link in those emails.
                    </p>

                    <h2>Security, storage, and your choices</h2>
                    <p className={styles.text}>
                        AWS provides hosting, authentication, databases, email delivery, and security
                        services. Security reports and operational logs may contain page or resource
                        addresses, browser error details, and request information. Image-recognition
                        usage limits can record an IP address or account identifier and daily request
                        counts. These records help prevent abuse and control service usage.
                    </p>
                    <p className={styles.text}>
                        Browser storage remains until it expires or is cleared. Cloud account,
                        progress, subscription, analytics, and security records have separate
                        retention settings; clearing browser storage does not delete cloud records.
                        Service providers may process information outside your country, including
                        in the United States. We do not sell your personal information. We share
                        information with the providers described above to operate the features you use.
                    </p>
                    <p className={styles.text}>
                        To ask about access, correction, export, deletion, or how long particular
                        records are retained, contact
                        {" "}<a href="mailto:admin@anasequeira.org">admin@anasequeira.org</a>.
                        You can change cookie preferences at any time without deleting your account.
                    </p>

                    <p className={styles.updated}>Last updated: September 20, 2026</p>
                </section>
            </main>

            <Footer />
        </div>
    )
}
